/**
 * 训练生成器（§7 / §8 / §12.3 / §13.1 / §15）。
 *
 * 由「序列槽位模板 + 当前伤情 + 准备度结果 + 动作库 + 历史重量」确定性生成今日训练：
 *   1. 计算每个动作的派生可用性（evaluateExerciseEligibility）；
 *   2. 按槽位解析动作：首选可用 → 保守回退搜索 → 无则删除（不伪造）；
 *   3. 应用准备度调整：体量倍率（组数）、负荷倍率（建议重量）、RIR 目标、力竭允许；
 *   4. 脚部略差：删除站姿动作；脚部明显变差：仅坐姿/卧姿高稳定保守模板；
 *   5. 关节疼痛部位：剔除刺激相关肌群的动作；
 *   6. E 日：总组数 ≤ 正式日参考 70%，全部 RIR ≥ 2、禁止力竭；
 *   7. 输出建议重量（历史 > 初始表 > null=首次校准）与预计时长。
 *
 * 所有输出带 reasonCodes 与 generationRuleVersion（审计）。
 */

import type {
  DerivedExerciseAvailability,
  Exercise,
  InjuryConstraint,
  WeightUnitScope,
} from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";
import type {
  GeneratedExercise,
  GenerateWorkoutInput,
  GenerateWorkoutResult,
} from "./interface";
import { evaluateExerciseEligibility, requiresUserConfirmation } from "./eligibility";
import { E_DAY_MAX_TOTAL_SETS, E_DAY_SETS_MULTIPLIER, type PlanSlot } from "./templates";
import { startingWeightFor } from "./startingWeights";
import type { ReadinessResult } from "./interface";

// ---------------------------------------------------------------------------
// 口径与取整
// ---------------------------------------------------------------------------

/** 每个动作的重量口径（决策 14.15）：由递增类型派生，保存原始口径不换算。 */
export function scopeForIncrementType(incrementType: Exercise["incrementType"]): WeightUnitScope {
  switch (incrementType) {
    case "per_dumbbell":
      return "per_dumbbell";
    case "barbell_total":
      return "total";
    case "bodyweight":
      return "bodyweight";
    case "machine_stack":
    default:
      return "machine_display";
  }
}

/** 组数取整（决策 14.5）：复合 ≥2 组、孤立 ≥1 组。 */
function clampMinSets(exercise: Exercise, sets: number): number {
  const min = exercise.compoundType === "compound" ? 2 : 1;
  return Math.max(min, Math.round(sets));
}

/** 建议重量 × 负荷倍率后保留 1 位小数。 */
const round1 = (v: number) => Math.round(v * 10) / 10;

// ---------------------------------------------------------------------------
// 关节疼痛部位过滤（§8「关节疼痛」：删除刺激相关关节的动作）
// ---------------------------------------------------------------------------

/**
 * 关节疼痛部位 → 需剔除的动作（§8「关节疼痛」：删除刺激相关关节的动作）。
 * 匹配规则（确定性、保守）：
 *   - 疼痛部位文本命中肌肉名（双向包含）；
 *   - 常用部位别名映射到动作模式：肩→推肩类/三角肌，肘→手臂屈伸，膝→腿屈伸/弯举，髋→髋外展/内收。
 */
const PAIN_LOCATION_PATTERNS: ReadonlyArray<{ keywords: readonly string[]; patterns: readonly string[]; muscles: readonly string[] }> = [
  { keywords: ["肩", "肩膀"], patterns: ["vertical_push"], muscles: ["三角肌"] },
  { keywords: ["肘"], patterns: ["arm_curl", "arm_extension"], muscles: ["肱二头肌", "肱三头肌"] },
  { keywords: ["膝"], patterns: ["knee_extension", "knee_flexion"], muscles: ["股四头肌", "腘绳肌"] },
  { keywords: ["髋", "胯"], patterns: ["hip_abduction", "hip_adduction"], muscles: ["臀中肌", "髋内收肌"] },
];

function touchesPainLocations(exercise: Exercise, locations: readonly string[]): boolean {
  if (locations.length === 0) return false;
  const muscles = [...exercise.primaryMuscles, ...exercise.secondaryMuscles].map((m) => m.toLowerCase());
  return locations.some((loc) => {
    const l = loc.toLowerCase();
    // 1) 肌肉名双向包含
    if (muscles.some((m) => m.includes(l) || l.includes(m))) return true;
    // 2) 部位别名 → 动作模式/肌群（保守：任一命中即剔除）
    return PAIN_LOCATION_PATTERNS.some(
      (rule) =>
        rule.keywords.some((k) => loc.includes(k)) &&
        (rule.patterns.includes(exercise.movementPattern) ||
          rule.muscles.some((m) => muscles.some((mus) => mus.includes(m) || m.includes(mus)))),
    );
  });
}

// ---------------------------------------------------------------------------
// 保守模板（§6.5 / §8 脚部明显变差）
// ---------------------------------------------------------------------------

/** 脚部明显变差：只保留完全坐姿/卧姿/胸托 + 高稳定 + 无前足负荷的动作。 */
function isConservativeEligible(exercise: Exercise): boolean {
  return (
    exercise.posture !== "standing" &&
    exercise.stabilityLevel === "high" &&
    exercise.forefootLoad === "none_or_minimal" &&
    exercise.balanceDemand === "low"
  );
}

export function requiresConservativeTemplate(planType: ReadinessResult["planType"]): boolean {
  return planType === "injury_conservative";
}

/** 脚部略差：默认删除需要站姿稳定的动作（§8）。 */
function allowsPostureForInjuryReduced(exercise: Exercise): boolean {
  return exercise.posture !== "standing";
}

// ---------------------------------------------------------------------------
// 槽位解析
// ---------------------------------------------------------------------------

function preferredOrder(slot: PlanSlot, lastFormalExerciseIds: readonly string[] | undefined): string[] {
  if (!slot.rotateRecent || slot.rotateRecent.length === 0 || !lastFormalExerciseIds) return slot.preferredIds;
  const recent = new Set(lastFormalExerciseIds);
  const first = slot.rotateRecent.filter((id) => recent.has(id)); // 上次已用 → 靠后
  const rest = slot.preferredIds.filter((id) => !first.includes(id));
  return [...rest, ...first];
}

interface ResolvedSlot {
  slot: PlanSlot;
  exercise: Exercise | null;
  confirmationRequired: boolean;
  skipReason: string | null;
}

function resolveSlot(
  slot: PlanSlot,
  library: Exercise[],
  byId: Map<string, Exercise>,
  eligible: Map<string, boolean>,
  usedIds: Set<string>,
  lastFormalExerciseIds: readonly string[] | undefined,
  injury: InjuryConstraint,
  conservativeOnly: boolean,
  injuryReducedOnly: boolean,
  painLocations: readonly string[],
): ResolvedSlot {
  const pick = (e: Exercise): boolean =>
    !usedIds.has(e.id) &&
    eligible.get(e.id) === true &&
    (!conservativeOnly || isConservativeEligible(e)) &&
    (!injuryReducedOnly || allowsPostureForInjuryReduced(e)) &&
    !touchesPainLocations(e, painLocations);

  // 1) 首选列表
  for (const id of preferredOrder(slot, lastFormalExerciseIds)) {
    const e = byId.get(id);
    if (e && pick(e)) {
      return { slot, exercise: e, confirmationRequired: requiresUserConfirmation(e, injury), skipReason: null };
    }
  }
  // 2) 保守回退搜索
  const candidates = library.filter(
    (e) =>
      pick(e) &&
      (slot.muscle == null || e.primaryMuscles.some((m) => m === slot.muscle)) &&
      (slot.pattern == null || e.movementPattern === slot.pattern) &&
      (!slot.isolationOnly || e.compoundType === "isolation"),
  );
  candidates.sort((a, b) => {
    const st = (x: Exercise) => (x.stabilityLevel === "high" ? 0 : x.stabilityLevel === "medium" ? 1 : 2);
    const fl = (x: Exercise) => (x.forefootLoad === "none_or_minimal" ? 0 : x.forefootLoad === "low" ? 1 : 2);
    const bl = (x: Exercise) => (x.balanceDemand === "low" ? 0 : x.balanceDemand === "medium" ? 1 : 2);
    const iso = (x: Exercise) => (x.compoundType === "isolation" ? 0 : 1);
    return iso(a) - iso(b) || st(a) - st(b) || fl(a) - fl(b) || bl(a) - bl(b);
  });
  const picked = candidates[0] ?? null;
  if (picked) {
    return { slot, exercise: picked, confirmationRequired: requiresUserConfirmation(picked, injury), skipReason: null };
  }
  // 3) 无可用 → 删除槽位
  return { slot, exercise: null, confirmationRequired: false, skipReason: "SLOT_DROPPED_NO_ELIGIBLE_EXERCISE" };
}

// ---------------------------------------------------------------------------
// 建议重量
// ---------------------------------------------------------------------------

function suggestedWeightFor(
  exercise: Exercise,
  readiness: ReadinessResult,
  lastWeights: ReadonlyMap<string, { weightKg: number; weightUnitScope: WeightUnitScope }> | undefined,
): { weightKg: number | null; weightUnitScope: WeightUnitScope } {
  const last = lastWeights?.get(exercise.id);
  const base = last?.weightKg ?? startingWeightFor(exercise.id)?.weightKg ?? null;
  const scope = last?.weightUnitScope ?? startingWeightFor(exercise.id)?.weightUnitScope ?? scopeForIncrementType(exercise.incrementType);
  const weightKg = base == null ? null : round1(base * readiness.loadMultiplier);
  return { weightKg, weightUnitScope: scope };
}

// ---------------------------------------------------------------------------
// 目标 RIR / 力竭 调整（§8 / §9 / §13）
// ---------------------------------------------------------------------------

function targetRirFor(
  exercise: Exercise,
  planType: ReadinessResult["planType"],
): number {
  switch (planType) {
    case "lightly_reduced":
      // §8 轻微降量：复合动作保持 RIR 2–3
      return exercise.compoundType === "compound" ? Math.max(exercise.targetRir, 3) : exercise.targetRir;
    case "reduced":
    case "injury_reduced":
    case "injury_conservative":
    case "deload":
      // §8/§13.2：所有动作保持 RIR 2–3
      return Math.max(exercise.targetRir, 3);
    case "optional_light_day":
      // §13.1：所有动作至少保留 RIR 2
      return Math.max(exercise.targetRir, 2);
    default:
      return exercise.targetRir;
  }
}

function allowFailureFor(
  exercise: Exercise,
  planType: ReadinessResult["planType"],
  readiness: ReadinessResult,
): boolean {
  if (!readiness.allowFailure) return false;
  if (planType === "optional_light_day" || planType === "deload" || planType === "injury_conservative") return false;
  return exercise.failureAllowedLastSet;
}

// ---------------------------------------------------------------------------
// 预计时长（§6.2：每组 ≈40s 执行 + 休息）
// ---------------------------------------------------------------------------

export function estimateExerciseMinutes(exercises: ReadonlyArray<{ targetSets: number; restSeconds: number }>): number {
  const totalSets = exercises.reduce((a, e) => a + e.targetSets, 0);
  const restSec = exercises.reduce((a, e) => a + e.targetSets * e.restSeconds, 0);
  return Math.max(1, Math.round((totalSets * 40 + restSec) / 60));
}

// ---------------------------------------------------------------------------
// 主入口
// ---------------------------------------------------------------------------

export function generateWorkoutPlan(input: GenerateWorkoutInput): GenerateWorkoutResult {
  const { sequenceCode, readiness, injury, exerciseLibrary } = input;
  const planType = readiness.planType;
  const lastWeights = input.lastWeights ?? new Map();
  const lastFormalExerciseIds = input.lastFormalExerciseIds ?? [];
  const painLocations = input.jointPainLocations ?? [];
  const reasonCodes: string[] = [];

  const eligibilityByExerciseId = new Map<string, boolean>();
  const availabilityById = new Map<string, DerivedExerciseAvailability>();
  const usedIds = new Set<string>();
  const byId = new Map(exerciseLibrary.map((e) => [e.id, e]));

  for (const e of exerciseLibrary) {
    const avail = evaluateExerciseEligibility({ exercise: e, injury });
    availabilityById.set(e.id, avail);
    eligibilityByExerciseId.set(e.id, avail.isEnabledForCurrentInjury);
  }

  // 准备度调整信号
  if (readiness.reasonCodes.length > 0) reasonCodes.push(...readiness.reasonCodes);
  if (planType !== "normal") reasonCodes.push(`PLAN_TYPE:${planType}`);

  // 关节疼痛过滤（§8）
  if (painLocations.length > 0) reasonCodes.push(`JOINT_PAIN_FILTER:${painLocations.join("+")}`);

  const conservativeOnly = requiresConservativeTemplate(planType);
  const injuryReducedOnly = planType === "injury_reduced";
  if (conservativeOnly) reasonCodes.push("CONSERVATIVE_TEMPLATE_ONLY");
  if (injuryReducedOnly) reasonCodes.push("STANDING_EXCLUDED");

  const isEDay = Boolean(input.isOptionalLightDay) || sequenceCode === "E" || planType === "optional_light_day";

  const slots = input.slots;
  const resolved: ResolvedSlot[] = [];
  for (const slot of slots) {
    const r = resolveSlot(
      slot,
      exerciseLibrary,
      byId,
      eligibilityByExerciseId,
      usedIds,
      planType === "normal" ? lastFormalExerciseIds : undefined,
      injury,
      conservativeOnly,
      injuryReducedOnly,
      painLocations,
    );
    // 槽位逐个解析：已解析的动作立即计入「已用」，保证后续槽位不重复（§16.8 同源原则）
    if (r.exercise) usedIds.add(r.exercise.id);
    resolved.push(r);
  }

  // 明显降量：可删除一个次要动作（§8「可删除一个次要动作」；决策 14.5 删除优先级取本日最后一个槽位）
  if (planType === "reduced") {
    for (let i = resolved.length - 1; i >= 0; i--) {
      if (resolved[i]!.exercise) {
        resolved[i] = { slot: resolved[i]!.slot, exercise: null, confirmationRequired: false, skipReason: "REMOVED_MINOR_EXERCISE_REDUCED_DAY" };
        reasonCodes.push("REMOVED_MINOR_EXERCISE_REDUCED_DAY");
        break;
      }
    }
  }

  // 生成动作列表
  const exercises: GeneratedExercise[] = [];
  for (const r of resolved) {
    if (!r.exercise) {
      reasonCodes.push(`${r.slot.id}:${r.skipReason ?? "EMPTY"}`);
      continue;
    }
    const e = r.exercise;
    usedIds.add(e.id);

    let targetSets = clampMinSets(e, e.defaultSets * readiness.volumeMultiplier);
    if (isEDay) {
      // E 日：组数约为正式日的 60%–70%，全部动作至少保留 RIR 2、禁止力竭
      targetSets = clampMinSets(e, Math.max(2, Math.round(e.defaultSets * E_DAY_SETS_MULTIPLIER)));
    }

    const weight = suggestedWeightFor(e, readiness, lastWeights);
    const avail = availabilityById.get(e.id);
    exercises.push({
      exercise: e,
      isEnabledForCurrentInjury: true,
      unmetConditions: avail?.unmetConditions ?? [],
      requiresUserConfirmation: r.confirmationRequired,
      suggestedWeightKg: weight.weightKg,
      weightUnitScope: weight.weightUnitScope,
      targetSets,
      repMin: e.repMin,
      repMax: e.repMax,
      targetRir: targetRirFor(e, isEDay ? "optional_light_day" : planType),
      restSeconds: e.restSeconds,
      allowFailureLastSet: allowFailureFor(e, isEDay ? "optional_light_day" : planType, readiness),
    });
  }

  // E 日总组数上限（60%–70%，保守取 70% 上限向下取整）
  if (isEDay) {
    let total = exercises.reduce((a, x) => a + x.targetSets, 0);
    while (total > E_DAY_MAX_TOTAL_SETS) {
      const heaviest = [...exercises].sort((a, b) => b.targetSets - a.targetSets)[0];
      if (!heaviest || heaviest.targetSets <= 2) break;
      heaviest.targetSets -= 1;
      total -= 1;
      reasonCodes.push("E_DAY_SET_CAP_ADJUSTED");
    }
    if (exercises.length === 0) reasonCodes.push("E_DAY_NO_ELIGIBLE_EXERCISES");
  }

  return {
    planType,
    exercises,
    reasonCodes,
    generationRuleVersion: RULES_VERSION,
    estimatedMinutes: estimateExerciseMinutes(exercises),
  };
}